import { Injectable, inject } from '@angular/core';

import { ApiService } from '@frontend/sports/common/api-utils';
import { AccaBoostConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { ExpiringCache, ExpiringCacheFactory } from '@frontend/sports/common/core/utils/expiring-cache';
import { Observable, catchError, map, of } from 'rxjs';

import { AccaBoostToken } from '../../betslip/modules/reward-tokens/reward-tokens.model';
import { AccaBoostSitecoreData, AccaBoostTokenData, AccaBoostTokensResponse } from './acca-boost-data.model';
import { AccaBoostTokenFactoryService } from './acca-boost-token-factory.service';

@Injectable({ providedIn: 'root' })
export class AccaBoostTokenLoaderService {
    private apiService = inject(ApiService);
    private accaBoostConfig = inject(AccaBoostConfig);
    private factoryService = inject(AccaBoostTokenFactoryService);

    private readonly tokenUrl = 'crmOffers/GetAccaBoost';
    private readonly configUrl = 'crmOffers/GetAccaBoostContent';
    private readonly configCache: ExpiringCache;
    private readonly logger: SportsRemoteLogger;

    constructor() {
        const accaBoostConfig = this.accaBoostConfig;
        const cacheFactory = inject(ExpiringCacheFactory);
        const loggerFactory = inject(LoggerFactory);

        this.configCache = cacheFactory.create(accaBoostConfig.sitecoreTokenDataCacheTime * 60);

        this.logger = loggerFactory.getLogger('AccaBoostTokenLoaderService');
    }

    // TODO: @Andriy this should not reference betslip project directly?
    loadTokens(): Observable<AccaBoostToken[]> {
        const source = this.accaBoostConfig.isEnabled ? this.fetchTokens() : of(undefined);

        return source.pipe(map((data) => (data ? this.mapTokens(data.tokens) : [])));
    }

    loadTokenData(sitecorePath: string): Observable<AccaBoostSitecoreData> {
        const source = this.apiService.get<AccaBoostSitecoreData>(this.configUrl, {
            sitecorePath,
        });

        return this.configCache.getOrCreate(sitecorePath, source);
    }

    private mapTokens(data: AccaBoostTokenData[]): AccaBoostToken[] {
        return data.map((token) => this.factoryService.map(token));
    }

    private fetchTokens(): Observable<AccaBoostTokensResponse | undefined> {
        return this.apiService.get<AccaBoostTokensResponse>(this.tokenUrl).pipe(
            catchError((err) => {
                this.logger.error(err, `AccaBoostTokenLoaderService - failed to get acca boost tokens.`);

                return of(undefined);
            }),
        );
    }
}
